import { getLastPrice, getHostName } from '../../../utils/Utils';
import { normalizeMarketplaceKey } from '../../Campaign/marketplaceOperatorSettings';

// Pure helpers for the Marketplace Operator list and sellers views. The summary
// fields (mp_*) and competitor fields (marketplace_key, is_first_party) are
// produced by pricerest; nothing here talks to the API.
//
// Own stores (is_own_store rows, mp_own_stores): the operator's own shops on other
// marketplaces. They are never competitors: pricerest keeps them out of mp_marketplaces,
// rank and best price, and here they get their own sellers group and their own band marker.

// Accepts the object returned by readMarketplaceOperator, or a bare key array.
const configuredCompetitors = (config) => {
    const raw = Array.isArray(config) ? config : config?.competitor_marketplaces;
    const keys = [];
    (Array.isArray(raw) ? raw : []).forEach((item) => {
        const key = normalizeMarketplaceKey(typeof item === 'string' ? item.trim() : item);
        if (key && !keys.includes(key)) keys.push(key);
    });
    return keys;
};

// Twin of pricerest's ordered_marketplace_keys: configured keys first in
// configured order (even when no row has them), then any other marketplace
// present on a row, alphabetically. An empty config lists everything present.
export const orderedMarketplaceKeys = (rows, config) => {
    const configured = configuredCompetitors(config);
    const extras = new Set();
    (rows ?? []).forEach((row) => {
        const marketplaces = row?.mp_marketplaces;
        if (!marketplaces || typeof marketplaces !== 'object') return;
        Object.keys(marketplaces).forEach((key) => {
            if (key && !configured.includes(key)) extras.add(key);
        });
    });
    return [...configured, ...Array.from(extras).sort()];
};

// Currency of the prices the marketplace profile shows for a list row or a detail
// summary: the summary's mp_currency (the owner's, or the first competitor's when the
// owner is a placeholder with none), then the row's own currency.
export const marketplaceCurrency = (row) => row?.mp_currency || row?.currency || undefined;

// 'no_price' | 'alone' | 'leader' | 'behind'
export const marketplaceStatus = (row) => {
    if (row?.mp_buybox_price == null) return 'no_price';
    if (row.mp_rank_total === 1) return 'alone';
    if (row.mp_rank === 1) return 'leader';
    return 'behind';
};

export const formatMarketplaceRank = (row) => {
    if (row?.mp_rank == null || row?.mp_rank_total == null) return '';
    return `${row.mp_rank}/${row.mp_rank_total}`;
};

const finitePrice = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : null);

// Delivery time ("termin") of an offer, written by the scraper on the price row for
// campaigns with settings.track_delivery_time: dispatch_days (days until the seller
// hands it to the carrier), delivery_date / delivery_date_max (ISO yyyy-mm-dd).
export const isDeliveryTracked = (campaignSettings) => !!campaignSettings?.track_delivery_time;

const isDayCount = (value) => typeof value === 'number' && Number.isInteger(value) && value >= 0;

// '2026-11-30' -> '30.11'; anything else is shown as it came.
const shortDate = (iso) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
    return match ? `${match[3]}.${match[2]}` : (iso || '');
};

// One short line for a delivery dict, e.g. "23 günde kargoda · Teslim 30.11", or null
// when it says nothing. t is i18next's t. isBuybox marks a delivery time that belongs to
// the buybox offer rather than to the cheapest one shown next to it.
export const formatDelivery = (delivery, t, isBuybox = false) => {
    if (!delivery || typeof delivery !== 'object') return null;
    const parts = [];
    if (isDayCount(delivery.dispatch_days)) {
        parts.push(delivery.dispatch_days === 0
            ? t('product.marketplace.dispatch_today')
            : t('product.marketplace.dispatch_days', { count: delivery.dispatch_days }));
    }
    if (delivery.delivery_date) {
        const date = delivery.delivery_date_max
            ? `${shortDate(delivery.delivery_date)}–${shortDate(delivery.delivery_date_max)}`
            : shortDate(delivery.delivery_date);
        parts.push(t('product.marketplace.delivery_by', { date }));
    }
    if (!parts.length) return null;
    const text = parts.join(' · ');
    return isBuybox ? `${text} (${t('product.marketplace.delivery_buybox')})` : text;
};

// Every marketplace of a row's mp_marketplaces as {key, min_price, seller, count,
// counted, url, delivery}, ascending by min_price; entries without a price come last, and
// ties keep key order. An entry without a counted flag counts, like heroStripItems.
export const sortedMarketplaceEntries = (row) => {
    const marketplaces = row?.mp_marketplaces;
    if (!marketplaces || typeof marketplaces !== 'object') return [];
    return Object.keys(marketplaces)
        .filter((key) => !!key)
        .map((key) => {
            const group = marketplaces[key] && typeof marketplaces[key] === 'object' ? marketplaces[key] : {};
            return {
                key,
                min_price: finitePrice(group.min_price),
                seller: group.seller || null,
                count: typeof group.count === 'number' ? group.count : 0,
                counted: group.counted !== false,
                url: group.url || null,
                delivery: group.delivery && typeof group.delivery === 'object' ? group.delivery : null,
                delivery_is_buybox: !!group.delivery_is_buybox,
            };
        })
        .sort((a, b) => {
            if ((a.min_price == null) !== (b.min_price == null)) return a.min_price == null ? 1 : -1;
            if (a.min_price != null && a.min_price !== b.min_price) return a.min_price - b.min_price;
            if (a.key === b.key) return 0;
            return a.key < b.key ? -1 : 1;
        });
};

// The row's own stores (mp_own_stores) as {key, price, url, seller, delivery}, in the
// given order (pricerest orders them by key); entries without a finite price or a key
// are dropped. Own stores are not in mp_marketplaces and never count as competitors.
export const ownStoreEntries = (row) => {
    const stores = row?.mp_own_stores;
    if (!Array.isArray(stores)) return [];
    return stores
        .filter((store) => store && store.key && finitePrice(store.price) != null)
        .map((store) => ({
            key: store.key,
            price: store.price,
            url: store.url || null,
            seller: store.seller || null,
            delivery: store.delivery && typeof store.delivery === 'object' ? store.delivery : null,
        }));
};

// The cheapest and the most expensive priced marketplace of a row, picked from
// the counted entries (all entries when none is counted). total is the number of
// marketplaces in mp_marketplaces. With a single priced marketplace both point at
// the same key; with none both are null.
export const cheapestAndPriciestMarketplaces = (row) => {
    const entries = sortedMarketplaceEntries(row);
    const counted = entries.filter((entry) => entry.counted);
    const pool = (counted.length > 0 ? counted : entries).filter((entry) => entry.min_price != null);
    const pick = (entry) => (entry ? { key: entry.key, min_price: entry.min_price, count: entry.count } : null);
    return {
        cheapest: pick(pool[0]),
        priciest: pick(pool[pool.length - 1]),
        total: entries.length,
    };
};

const unset = (value) => (value === undefined || value === null ? '' : value);

// List query params for the marketplace filters; '' means the filter is off.
export const marketplaceQueryFilters = (filters) => ({
    mp_cheaper_on: unset(filters?.mp_cheaper_on),
    mp_min_diff_pct: unset(filters?.mp_min_diff_pct),
    mp_first_party: unset(filters?.mp_first_party),
    mp_status: unset(filters?.mp_status),
    mp_buybox: unset(filters?.mp_buybox),
});

// The same filters as list request params, with only the ones that are set: a
// blank value is left out, so a request from any other profile carries exactly
// the params it carried before the marketplace filters existed.
export const marketplaceListParams = (filters) => {
    const params = {};
    Object.entries(marketplaceQueryFilters(filters)).forEach(([key, value]) => {
        if (typeof value === 'string' && value.trim() === '') return;
        params[key] = value;
    });
    return params;
};

// True when any mp_* list filter is set.
export const hasMarketplaceFilter = (filters) => Object.keys(marketplaceListParams(filters)).length > 0;

// Display-name overrides, consulted before the generic rule. pricerest's
// MARKETPLACE_DISPLAY_NAMES must hold the same entries.
export const MARKETPLACE_DISPLAY_NAMES = {amazon: 'Amazon Türkiye', bauhaus: 'Bauhaus'};

// Display label of a normalized marketplace key. A key in
// MARKETPLACE_DISPLAY_NAMES uses its override; any other key: '_' -> '.', first
// character upper-cased (hepsiburada -> Hepsiburada). pricerest's
// marketplace_label applies the same rule to the Excel headers.
export const marketplaceLabel = (key) => {
    if (!key) return '';
    const name = String(key);
    if (Object.prototype.hasOwnProperty.call(MARKETPLACE_DISPLAY_NAMES, name)) return MARKETPLACE_DISPLAY_NAMES[name];
    const label = name.replace(/_/g, '.');
    return label.charAt(0).toUpperCase() + label.slice(1);
};

// Counts for the "Cheaper on" dropdown, from list rows: any = rows behind the
// buybox (mp_rank > 1), byKey[key] = rows whose mp_cheaper_marketplaces lists the key
// (once per row, however often it repeats).
export const cheaperOnCounts = (rows) => {
    const counts = {any: 0, byKey: {}};
    (rows ?? []).forEach((row) => {
        if (!row) return;
        if (typeof row.mp_rank === 'number' && row.mp_rank > 1) counts.any += 1;
        const keys = Array.isArray(row.mp_cheaper_marketplaces) ? row.mp_cheaper_marketplaces : [];
        new Set(keys.filter((key) => !!key)).forEach((key) => {
            counts.byKey[key] = (counts.byKey[key] || 0) + 1;
        });
    });
    return counts;
};

// The counts cache: recomputed from rows only for a load made without any mp_*
// filter (so the numbers do not shrink to the filtered set); otherwise, or while
// no rows have arrived yet, the cached counts are kept.
export const cheaperOnCountsCache = (cached, rows, mpFilterActive) => {
    if (mpFilterActive || !rows || rows.length === 0) return cached;
    return cheaperOnCounts(rows);
};

// Options of the "Cheaper on" dropdown: "<label> (N)", the any option first. An option
// with N = 0 is hidden unless it is the selected one. Without counts every option is
// listed without a number.
export const cheaperOnOptionList = ({keys, counts, selected, anyLabel, labelOf}) => {
    const entries = [{label: anyLabel, value: 'any', n: counts ? counts.any : null}]
        .concat((keys ?? []).map((key) => ({label: labelOf(key), value: key, n: counts ? (counts.byKey?.[key] || 0) : null})));
    return entries
        .filter((entry) => entry.n == null || entry.n > 0 || entry.value === selected)
        .map((entry) => ({label: entry.n == null ? entry.label : `${entry.label} (${entry.n})`, value: entry.value}));
};

// Label of a marketplace key on a campaign: the own marketplace shows the
// campaign's Seller Code (settings.seller_code) when one is set, any other key
// (or an own key without a Seller Code) falls back to marketplaceLabel.
export const ownAwareMarketplaceLabel = (key, campaignSettings) => {
    const own = normalizeMarketplaceKey(campaignSettings?.marketplace_operator?.own_marketplace_key ?? '');
    const sellerCode = typeof campaignSettings?.seller_code === 'string' ? campaignSettings.seller_code.trim() : '';
    if (key && own && sellerCode && normalizeMarketplaceKey(String(key)) === own) return sellerCode;
    return marketplaceLabel(key);
};

// The operator's own store name for labels: the campaign's trimmed Seller Code
// (settings.seller_code), or null when none is set.
export const operatorName = (campaignSettings) => {
    const code = campaignSettings?.seller_code;
    if (typeof code !== 'string') return null;
    const trimmed = code.trim();
    return trimmed || null;
};

// i18next options for the 1P labels: {{seller}} is the operator name; without one
// the context makes i18next pick the "<key>_generic" wording instead.
export const operatorTextOptions = (campaignSettings) => {
    const seller = operatorName(campaignSettings);
    return seller ? { seller } : { context: 'generic' };
};

// Latest in-stock price of a competitor row, or null when it is out of stock.
const inStockPrice = (row) => {
    const last = getLastPrice(row?.prices);
    if (!last || !(last.stock > 0)) return null;
    const value = Number(last.value);
    return Number.isFinite(value) && value > 0 ? value : null;
};

// Seller name without its marketplace prefix: "koctas-Usta El" -> "Usta El",
// "HepsiBurada-GOLD BILISIM" -> "GOLD BILISIM". The group header already names the
// marketplace. Twin of the helper MarketplaceRows.js keeps for the list view.
export const sellerDisplayName = (seller) => {
    if (!seller) return '';
    // Own store rows all carry the operator's seller name, so the marketplace tells them apart.
    if (seller.is_own_store && seller.marketplace_key) return marketplaceLabel(seller.marketplace_key);
    if (seller.seller_name) return seller.seller_name;
    const name = seller.name || '';
    if (seller.marketplace_key && name.includes('-')) return name.slice(name.indexOf('-') + 1);
    return name || getHostName(seller.product_url);
};

// Groups a product's seller rows by marketplace. Groups are ordered by their
// cheapest in-stock price (mpGroupMin) ascending, the own marketplace included at
// its price position; groups with no in-stock price follow, alphabetically, and the
// "Other" group (no marketplace key) is always last. Inside a group, rows are
// sorted by latest in-stock price (out of stock last). A group in collapsedKeys
// yields a single placeholder row (mpPlaceholder: true) instead of its sellers, so
// the group header still renders; the placeholder is hidden by CSS and carries the
// same group fields as a real row. mpGroupPosition is the 1-based position of a priced
// group in that order (null for the "Other" group and groups with no in-stock price).
// Own store rows (is_own_store) form one separate group, OWN_STORES_KEY, placed after
// every marketplace group (before "Other"); they stay out of their marketplace's stats
// and the group is never numbered.
export const OWN_STORES_KEY = 'own_stores';

const groupDelivery = (stat) => {
    const cheapest = getLastPrice(stat.minRow?.prices)?.delivery;
    if (cheapest) return { mpGroupMinDelivery: cheapest, mpGroupDeliveryIsBuybox: false };
    const buybox = getLastPrice(stat.buyboxRow?.prices)?.delivery;
    return { mpGroupMinDelivery: buybox || null, mpGroupDeliveryIsBuybox: !!buybox };
};

export const groupSellerRows = (rows, config, collapsedKeys) => {
    const collapsed = collapsedKeys ?? new Set();
    const own = normalizeMarketplaceKey(config?.own_marketplace_key ?? '');
    const configured = configuredCompetitors(config).filter((key) => key !== own);

    // Stable identity of a group; the own marketplace also takes keyless is_mine rows.
    const identityOf = (key, isMine, isOwnStore) => {
        if (isOwnStore) return OWN_STORES_KEY;
        if (key === own || (isMine && !key)) return `own_${own}`;
        return key ? `mp_${key}` : 'other';
    };

    const decorated = [];
    (rows ?? []).forEach((row) => {
        if (!row) return;
        if (row.is_parent_url && !row.is_mine) return;
        const key = normalizeMarketplaceKey(row.marketplace_key ?? '');
        const identity = identityOf(key, !!row.is_mine, !!row.is_own_store && !row.is_mine);
        const mpGroupKey = identity === OWN_STORES_KEY ? OWN_STORES_KEY : (identity.startsWith('own_') ? own : key);
        decorated.push({ row, identity, mpGroupKey, price: inStockPrice(row) });
    });

    const stats = new Map();
    decorated.forEach(({ row, identity, mpGroupKey, price }) => {
        const stat = stats.get(identity)
            ?? { identity, key: mpGroupKey, min: null, minRow: null, buyboxRow: null, count: 0, firstRow: row };
        stat.count += 1;
        if (price != null && !stat.buyboxRow && getLastPrice(row.prices)?.rank === 1) stat.buyboxRow = row;
        if (price != null && (stat.min == null || price < stat.min)) {
            stat.min = price;
            stat.minRow = row;
        }
        stats.set(identity, stat);
    });

    const configuredIndex = (stat) => {
        if (stat.identity.startsWith('own_') && stat.identity !== OWN_STORES_KEY) return -1;
        const index = configured.indexOf(stat.key);
        return index >= 0 ? index : configured.length;
    };
    // Marketplace groups first, then the own stores group, then "Other".
    const tail = (stat) => (stat.identity === 'other' ? 2 : (stat.identity === OWN_STORES_KEY ? 1 : 0));
    const groups = Array.from(stats.values()).sort((a, b) => {
        if (tail(a) !== tail(b)) return tail(a) - tail(b);
        if ((a.min == null) !== (b.min == null)) return a.min == null ? 1 : -1;
        if (a.min != null && a.min !== b.min) return a.min < b.min ? -1 : 1;
        if (a.min != null) {
            const ia = configuredIndex(a);
            const ib = configuredIndex(b);
            if (ia !== ib) return ia - ib;
        }
        if (a.key === b.key) return 0;
        return a.key < b.key ? -1 : 1;
    });
    // mpGroup is the DataTable groupField: unique per group and sortable in display order.
    const groupCode = new Map();
    groups.forEach((stat, index) => {
        groupCode.set(stat.identity, `${String(index).padStart(3, '0')}_${stat.key}`);
    });

    decorated.sort((a, b) => {
        const ga = groupCode.get(a.identity);
        const gb = groupCode.get(b.identity);
        if (ga !== gb) return ga < gb ? -1 : 1;
        const pa = a.price ?? Infinity;
        const pb = b.price ?? Infinity;
        if (pa === pb) return 0;
        return pa < pb ? -1 : 1;
    });

    // 1-based position of a group in the displayed price order. Only groups with an
    // in-stock price are numbered, and the "Other" group never is; the position does
    // not depend on which groups are collapsed.
    const groupPosition = new Map();
    let nextPosition = 1;
    groups.forEach((stat) => {
        if (stat.identity === 'other' || stat.identity === OWN_STORES_KEY || stat.min == null) return;
        groupPosition.set(stat.identity, nextPosition);
        nextPosition += 1;
    });

    const groupFields = (identity, mpGroupKey) => {
        const stat = stats.get(identity);
        const currencyRow = stat.minRow ?? stat.firstRow;
        return {
            mpGroup: groupCode.get(identity),
            mpGroupKey,
            mpGroupMin: stat.min,
            mpGroupMinSeller: stat.minRow ? sellerDisplayName(stat.minRow) : null,
            // The delivery time of the same offer whose price heads the group; when the site
            // gave none for it (Trendyol asks for the buybox offer only), the buybox offer's.
            ...groupDelivery(stat),
            mpGroupCurrency: getLastPrice(currencyRow?.prices)?.currency,
            mpGroupCount: stat.count,
            mpGroupPosition: groupPosition.get(identity) ?? null,
        };
    };

    const seen = new Set();
    const out = [];
    decorated.forEach(({ row, identity, mpGroupKey }) => {
        if (collapsed.has(mpGroupKey)) {
            if (seen.has(identity)) return;
            seen.add(identity);
            out.push({ ...groupFields(identity, mpGroupKey), mpPlaceholder: true });
            return;
        }
        out.push({ ...row, ...groupFields(identity, mpGroupKey) });
    });
    return out;
};

// Keys of the marketplace groups that start collapsed on the product detail Sellers
// tab: every group, except the ones holding the row a deep link (highlight_competitor_url)
// points at, so the linked seller is visible on arrival. A row matches by its own URL
// or its parent URL, ignoring case and a trailing slash, like the highlight itself.
export const initialCollapsedKeys = (rows, config, highlightUrl) => {
    const grouped = groupSellerRows(rows, config, new Set());
    const keys = new Set(grouped.map((row) => row.mpGroupKey));
    const normalize = (url) => (url ? String(url).trim().replace(/\/$/, '').toLowerCase() : '');
    const target = normalize(highlightUrl);
    if (!target) return keys;
    grouped.forEach((row) => {
        if (normalize(row.product_url) === target || normalize(row.parent_url) === target) {
            keys.delete(row.mpGroupKey);
        }
    });
    return keys;
};

// Sellers of one marketplace from the price-comparison sellers payload
// ({marketplaceKey: [sellers]}, keys case-preserved as 'HepsiBurada' or
// 'bauhaus.com.tr'). An exact key wins. Otherwise every key matching it
// case-insensitively, or by its normalized form, is merged and ordered by price
// (missing prices last) so the first seller stays the cheapest.
export const sellersForMarketplace = (data, key) => {
    if (!data || typeof data !== 'object' || typeof key !== 'string' || !key) return [];
    if (Array.isArray(data[key])) return data[key];
    const lower = key.toLowerCase();
    const normalized = normalizeMarketplaceKey(key);
    const merged = [];
    Object.keys(data).forEach((candidate) => {
        if (!Array.isArray(data[candidate])) return;
        if (candidate.toLowerCase() === lower || normalizeMarketplaceKey(candidate) === normalized) {
            merged.push(...data[candidate]);
        }
    });
    const priceOf = (seller) => (typeof seller?.price === 'number' && Number.isFinite(seller.price) ? seller.price : Infinity);
    return merged
        .map((seller, index) => ({ seller, index }))
        .sort((a, b) => (priceOf(a.seller) - priceOf(b.seller)) || (a.index - b.index))
        .map(({ seller }) => seller);
};

// Items of the product detail hero's marketplace strip, one per key in
// orderedMarketplaceKeys order. A configured marketplace with no rows on the
// product is an empty item. Only counted marketplaces can be cheaper than the
// buybox; extras are shown but never flagged.
export const heroStripItems = (summary, config) => {
    if (!summary || typeof summary !== 'object') return [];
    const marketplaces = summary.mp_marketplaces && typeof summary.mp_marketplaces === 'object'
        ? summary.mp_marketplaces : {};
    const buybox = typeof summary.mp_buybox_price === 'number' && Number.isFinite(summary.mp_buybox_price)
        ? summary.mp_buybox_price : null;
    return orderedMarketplaceKeys([summary], config).map((key) => {
        const group = marketplaces[key];
        const min = typeof group?.min_price === 'number' && Number.isFinite(group.min_price) ? group.min_price : null;
        const counted = group ? group.counted !== false : true;
        return {
            key,
            min_price: min,
            count: group?.count || 0,
            counted,
            cheaper: counted && buybox != null && min != null && min < buybox,
        };
    });
};

// --- Price band of the product detail hero ---
// One horizontal band: a dot per marketplace minimum, plus the operator's own (1P)
// price and the buybox price, on a linear scale. All of it is pure; the component
// only draws what these helpers return.

const BAND_DEFAULT_LABELS = { first_party: 'First party', buybox: 'Buybox' };
const BAND_TYPE_ORDER = { marketplace: 0, own_store: 1, first_party: 2, buybox: 3 };
const BAND_PAD_RATIO = 0.05;

// Every price on the band, in tie-break order: marketplaces (by price, then config
// order), then the own stores (mp_own_stores, in their given order), then the 1P price,
// then the buybox price. labels names the two non
// marketplace entries; a missing name falls back to a generic English one.
const bandEntries = (summary, config, labels) => {
    if (!summary || typeof summary !== 'object') return [];
    const names = { ...BAND_DEFAULT_LABELS, ...(labels || {}) };
    const marketplaces = summary.mp_marketplaces && typeof summary.mp_marketplaces === 'object'
        ? summary.mp_marketplaces : {};
    const entries = [];
    orderedMarketplaceKeys([summary], config).forEach((key) => {
        const group = marketplaces[key] && typeof marketplaces[key] === 'object' ? marketplaces[key] : {};
        const price = finitePrice(group.min_price);
        if (price == null) return;
        entries.push({
            type: 'marketplace',
            key,
            label: marketplaceLabel(key),
            price,
            counted: group.counted !== false,
            count: typeof group.count === 'number' ? group.count : 0,
            seller: group.seller || null,
        });
    });
    (Array.isArray(summary.mp_own_stores) ? summary.mp_own_stores : []).forEach((store) => {
        const price = finitePrice(store?.price);
        if (!store?.key || price == null) return;
        entries.push({
            type: 'own_store',
            key: store.key,
            label: marketplaceLabel(store.key),
            price,
            counted: true,
            count: 0,
            seller: store.seller || null,
            url: store.url || null,
            delivery: store.delivery && typeof store.delivery === 'object' ? store.delivery : null,
        });
    });
    const firstParty = finitePrice(summary.mp_first_party_price);
    if (firstParty != null) {
        entries.push({
            type: 'first_party', key: 'first_party', label: names.first_party, price: firstParty,
            counted: true, count: 0, seller: null,
        });
    }
    const buybox = finitePrice(summary.mp_buybox_price);
    if (buybox != null) {
        entries.push({
            type: 'buybox', key: 'buybox', label: names.buybox, price: buybox,
            counted: true, count: 0, seller: summary.mp_buybox_holder || null,
        });
    }
    return entries;
};

// Lowest-first (stable: ties keep tie-break order).
const sortBandEntries = (entries) => entries
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => (a.entry.price - b.entry.price)
        || (BAND_TYPE_ORDER[a.entry.type] - BAND_TYPE_ORDER[b.entry.type])
        || (a.index - b.index))
    .map(({ entry }) => entry);

// Scale of the band: min/max over every marketplace minimum AND the own store prices AND
// the 1P price AND the buybox price, with the name of whoever holds each extreme (a tie goes to the
// marketplace, then 1P). axisMin/axisMax add 5% of the span as padding on both sides
// (a lone price is padded by 5% of itself, at least 1) so a dot never sits on the
// edge. Without any priced marketplace or own store there is no band: null.
export const priceBandScale = (summary, labels) => {
    const entries = bandEntries(summary, null, labels);
    if (!entries.some((entry) => entry.type === 'marketplace' || entry.type === 'own_store')) return null;
    const sorted = sortBandEntries(entries);
    const low = sorted[0];
    // sorted is ascending, so the highest price is last; a tie keeps the first entry in tie-break order.
    const maxPrice = sorted[sorted.length - 1].price;
    const highest = sorted.find((entry) => entry.price === maxPrice);
    const span = highest.price - low.price;
    const pad = span > 0 ? span * BAND_PAD_RATIO : Math.max(Math.abs(low.price) * BAND_PAD_RATIO, 1);
    const axisMin = low.price >= 0 ? Math.max(0, low.price - pad) : low.price - pad;
    return {
        min: low.price,
        max: highest.price,
        minLabel: low.label,
        maxLabel: highest.label,
        minType: low.type,
        maxType: highest.type,
        axisMin,
        axisMax: highest.price + pad,
    };
};

// Markers of the band, lowest price first:
// {type: 'marketplace'|'own_store'|'first_party'|'buybox', key, label, price, pct (0..100 on the
// padded axis), cheaperThanBuybox, counted, count, seller}. Only a counted marketplace
// strictly below the buybox price is cheaperThanBuybox, like heroStripItems (an own
// store never is). The band needs a priced marketplace or own store: without one there
// are no markers at all.
export const priceBandMarkers = (summary, config, labels) => {
    const entries = bandEntries(summary, config, labels);
    const scale = priceBandScale(summary, labels);
    if (!scale) return [];
    const buybox = finitePrice(summary.mp_buybox_price);
    const range = scale.axisMax - scale.axisMin;
    return sortBandEntries(entries).map((entry) => ({
        ...entry,
        pct: Math.max(0, Math.min(100, ((entry.price - scale.axisMin) / range) * 100)),
        cheaperThanBuybox: entry.type === 'marketplace' && entry.counted && buybox != null && entry.price < buybox,
    }));
};

const LABEL_GAP_PX = 60;
const LABEL_ROWS = 2;
const LABEL_DEFAULT_WIDTH_PX = 600;

// Row of each marker's label above the band: 0 (nearest the band), 1, or null when
// the label does not fit and only the dot is shown. items: [{pct, priority}] in any
// order; the result is aligned with items. A label needs minGapPx (60) of free
// horizontal room on its row; markers are placed by priority (highest first, then
// left to right), each on the first row with room. An unknown width counts as 600px.
export const assignLabelRows = (items, widthPx, options) => {
    const minGapPx = options?.minGapPx ?? LABEL_GAP_PX;
    const rowCount = options?.rows ?? LABEL_ROWS;
    const width = typeof widthPx === 'number' && Number.isFinite(widthPx) && widthPx > 0 ? widthPx : LABEL_DEFAULT_WIDTH_PX;
    const list = Array.isArray(items) ? items : [];
    const result = list.map(() => null);
    const placed = Array.from({ length: rowCount }, () => []);
    list
        .map((item, index) => ({ index, px: (item.pct * width) / 100, priority: item.priority || 0 }))
        .sort((a, b) => (b.priority - a.priority) || (a.px - b.px) || (a.index - b.index))
        .forEach(({ index, px }) => {
            for (let row = 0; row < rowCount; row += 1) {
                if (placed[row].every((other) => Math.abs(px - other) >= minGapPx - 1e-9)) {
                    placed[row].push(px);
                    result[index] = row;
                    return;
                }
            }
        });
    return result;
};

// Text of the line under the band: whose price is compared and by how much.
// subject 'first_party' (the 1P price exists; uses mp_first_party_diff_pct) or
// 'buybox' (uses mp_diff_pct). pct is the absolute difference to the cheapest
// marketplace, direction says 'more' (pricier, red), 'less' (cheaper, green) or
// 'equal'. rank/total come from mp_rank / mp_rank_total (both or neither). Null when
// there is neither a percentage nor a rank to show.
export const priceBandSummary = (summary) => {
    if (!summary || typeof summary !== 'object') return null;
    const subject = finitePrice(summary.mp_first_party_price) != null ? 'first_party' : 'buybox';
    const diff = finitePrice(subject === 'first_party' ? summary.mp_first_party_diff_pct : summary.mp_diff_pct);
    const hasRank = typeof summary.mp_rank === 'number' && typeof summary.mp_rank_total === 'number';
    if (diff == null && !hasRank) return null;
    return {
        subject,
        pct: diff == null ? null : Math.abs(diff),
        direction: diff == null ? null : (diff > 0 ? 'more' : (diff < 0 ? 'less' : 'equal')),
        rank: hasRank ? summary.mp_rank : null,
        total: hasRank ? summary.mp_rank_total : null,
    };
};
